import * as React from "react";
import { DroppsCatalog, DroppsProduct } from "@/store";
import { BoxSchedule } from "./BoxSchedule";
import { sanitizeData } from "./RechargeDataCleanup";
import {
  QueryClient,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  applyPortalDiscountCode,
  createOnetimeItem,
  createSubscriptionItem,
  reactivateSubscription,
  removePortalDiscountCode,
} from "./RechargeApiActions";
import {
  Address,
  SubscriptionsOntimesAndInactives,
  RechargeAddress,
  RechargeItemRaw,
  BoxItem,
} from "./types";
import { UpdateBoxItemParams } from "./SelectedItemContext";
import { Overwrite } from "@/types/utility";
import { track } from "@/helpers/googleAnalytics";
import { useCustomer } from "../CustomerContext";
import { clearShopMoreSaveMoreCookie } from "@/helpers/emailCampaign";

const AccountContext = React.createContext<
  | {
      boxItems: BoxItem[];
      allItems: SubscriptionsOntimesAndInactives[];
      products: DroppsProduct[];
      addresses: Address[];
      boxSchedule: BoxSchedule;
    }
  | undefined
>(undefined);

type AccountProviderProps = {
  rechargeAddresses: RechargeAddress[];
  catalog: DroppsCatalog;
  subscriptionsAndOnetimes: RechargeItemRaw[];
  children: React.ReactNode;
};

const AccountContextProvider = ({
  children,
  subscriptionsAndOnetimes,
  catalog,
  rechargeAddresses,
}: AccountProviderProps) => {
  const customer = useCustomer();
  const { addresses, allItems, products, boxItems, boxSchedule } =
    React.useMemo(() => {
      const sanitized = sanitizeData(
        subscriptionsAndOnetimes,
        rechargeAddresses,
        catalog,
        customer?.isWholeSale === true
      );
      const boxSchedule: BoxSchedule = new BoxSchedule(sanitized.boxItems);
      return { ...sanitized, boxSchedule };
    }, [subscriptionsAndOnetimes, rechargeAddresses, catalog, customer]);

  const value = {
    boxItems,
    products,
    addresses,
    boxSchedule,
    allItems,
  };

  return (
    <AccountContext.Provider value={value}>{children}</AccountContext.Provider>
  );
};

function useAccountContext() {
  const context = React.useContext(AccountContext);
  if (context === undefined) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
}

function addrChangeSuccess(data: RechargeAddress, queryClient: QueryClient) {
  queryClient.setQueryData<RechargeAddress[]>(
    ["rechargeAddresses"],
    (oldData: RechargeAddress[] | undefined) => {
      return oldData?.map((o) => (o.id === data.id ? data : o));
    }
  );
}

export function useApplyDiscountCode() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (codeInfo: { addressId: number; discountName: string }) =>
      applyPortalDiscountCode(codeInfo.addressId, codeInfo.discountName),
    onSuccess: (data: RechargeAddress, { discountName }) => {
      track({
        name: "Portal Add Discount",
        discount: discountName,
      });
      addrChangeSuccess(data, queryClient);
    },
  });
}

export function useRemoveDiscountCode() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (addressId: number) => removePortalDiscountCode(addressId),
    onSuccess: (data: RechargeAddress) => {
      addrChangeSuccess(data, queryClient);
      track({
        name: "Portal Remove Discount",
      });
    },
  });
}

export function useReactivateSubscripiton() {
  const queryClient = useQueryClient();
  const products = useAccountContext().products;
  return useMutation({
    mutationFn: (subscriptionId: number) =>
      reactivateSubscription({ subscriptionId }),
    onSuccess: (data: RechargeItemRaw) => {
      track({ name: "Portal Reactivate Subscription", item: data });
      queryClient.setQueryData<RechargeItemRaw[]>(
        ["rechargeSubscriptions"],
        (oldData: RechargeItemRaw[] | undefined) => {
          const product = products.find(
            (x) => x.id === data.shopify_product_id
          );
          if (!product) {
            throw new Error(
              `Product ID: ${data.shopify_product_id}, Title:${data.product_title} not found`
            );
          }
          const oldDataExcludingInactiveSub = (oldData || []).filter(
            (x) => x.id !== data.id
          );
          return [...oldDataExcludingInactiveSub, data];
        }
      );
    },
  });
}

export type AddBoxItemParams = Overwrite<
  UpdateBoxItemParams,
  Required<Pick<UpdateBoxItemParams, "shopifyVariantId">>
>;

export function useAddNewBoxItem() {
  const queryClient = useQueryClient();
  const products = useAccountContext().products;
  const applyDiscount = useApplyDiscountCode();

  return useMutation({
    mutationFn: async (addItem: {
      params: AddBoxItemParams;
      address: Address;
      nextChargeDate: Date;
      sourceModule: string;
    }): Promise<RechargeItemRaw> => {
      const properties = {
        "properties[_source-module]": addItem.sourceModule,
      };

      let result: RechargeItemRaw;

      if (addItem.params.isSubscription && addItem.params.cadence) {
        result = await createSubscriptionItem({
          addressId: addItem.address.id,
          nextChargeDate: addItem.nextChargeDate,
          shopifyVariantId: addItem.params.shopifyVariantId,
          quantity: addItem.params.quantity ?? 1,
          cadence: addItem.params.cadence,
          properties,
        });
      } else {
        result = await createOnetimeItem({
          addressId: addItem.address.id,
          shopifyVariantId: addItem.params.shopifyVariantId,
          quantity: addItem.params.quantity ?? 1,
          nextChargeDate: addItem.nextChargeDate,
          properties,
        });
      }

      // Apply automatic discount if specified
      if (
        addItem.params.applyAutomaticDiscount &&
        addItem.params.discountCode &&
        addItem.params.isSubscription
      ) {
        try {
          await applyDiscount.mutateAsync({
            addressId: addItem.address.id,
            discountName: addItem.params.discountCode,
          });
          // Clear the cookie after successful discount application
          clearShopMoreSaveMoreCookie();
          // Track the discount application
          // track({
          //   name: "Portal Auto Discount Applied",
          //   discount: addItem.params.discountCode,
          //   sourceModule: addItem.sourceModule,
          // });
        } catch (error) {
          console.warn("Failed to apply automatic discount:", error);
          // Don't fail the entire operation if discount application fails
        }
      }

      return result;
    },
    onSuccess: (data) => {
      queryClient.setQueryData<RechargeItemRaw[]>(
        ["rechargeSubscriptions"],
        (oldData: RechargeItemRaw[] | undefined) => {
          const product = products.find(
            (x) => x.id === data.shopify_product_id
          );
          if (!product) {
            throw new Error(
              `Product ID: ${data.shopify_product_id}, Title:${data.product_title} not found`
            );
          }
          return [...(oldData || []), data];
        }
      );
    },
  });
}

export { AccountContextProvider, useAccountContext };
